import moment from "moment/moment";
import { createContext,useContext,useEffect,useRef,useState } from "react";
import useToastify from "../hooks/useToastify.js";
import LoaderController from "../Controllers/LoaderController.js";
import { createQuickPrintsData, getQuickPrintsData, updateQuickPrintsData, uploadImageToS3 } from "../AppComponents/QuickPrints/QuickPrintsAPIs.js";
import { API } from "aws-amplify";
import getCalendarTypes from "../Apps/Speak2ContentAPIs/CalendarTypes.js";
import { getCalendarCategories } from "../Apps/Speak2ContentAPIs/ServiceCategories.js";
import { getRestaurantsList } from './../Apps/Speak2ContentAPIs/Assets';
import { resolveFacilityContext } from "../Apps/Speak2ContentAPIs/Speak2Profile.js";
import { handleGetSavedTemplates } from "../Apis/Newsletter/UserTemplates_APIs.js";
import { extractBodyFieldLayoutFromElements } from "../AppComponents/QuickPrints/quickPrintsUtils.js";
import AuthContext from "./AuthContext.js";
import CorporateFacilityContext from "./CorporateFacilityContext.js";

const QuickPrintsContext=createContext();

export default QuickPrintsContext;

const EMPTY_QUICK_PRINTS_DATA = {
    LogoImageFileObject:null,
    LogoImage:"",
    LogoImageName:"",
    LogoImageContentType:"",
    Color1:"#000000",
    Color2:"#000000",
    Text:"",
    SubText:"",
};

const DEFAULT_DAILY_FIELDS = {
    title: true,
    time: true,
    location: true,
    short: false,
    long: false,
};

export const QuickPrintsContextProvider = ({children}) => {
    const { UserId, IsAuthenticated, IsAuthChecking } = useContext(AuthContext);
    const corporateCtx = useContext(CorporateFacilityContext);
    const activeFacilityId = corporateCtx?.activeFacility?.facilityId ?? null;

    const [QuickPrintsDataObject,setQuickPrintsDataObject]=useState({
        ...EMPTY_QUICK_PRINTS_DATA,
    });
    const [QuickPrintTemplateMeta, setQuickPrintTemplateMeta] = useState(null);
    const [DailyFieldsSelection, setDailyFieldsSelection] = useState({ ...DEFAULT_DAILY_FIELDS });
    const templateLoadRequestIdRef = useRef(0);
    const [QuickPrintTemplatesList, setQuickPrintTemplatesList] = useState([]);
    const [selectedQPTemplateId, setSelectedQPTemplateId] = useState(null);
    const [FacilityIdAndTimeZone,setFacilityIdAndTimeZone]=useState(null);
    const [CalendarTypes,setCalendarTypes]=useState([]);
    const [SelectedCalendarTypes,setSelectedCalendarTypes]=useState([]);
    const [CategoiresList,setCategoiresList]=useState([]);
    const [SelectedCalendarCategories,setSelectedCalendarCategories]=useState([]);
    const [RestaurantsList,setRestaurantsList]=useState([]);
    const [SelectedResturant,setSelectedResturant]=useState(null);
    const LogoImageRef = useRef(null);
    const {showNotification}=useToastify();

    const resetFacilityScopedState = () => {
        templateLoadRequestIdRef.current += 1;
        setQuickPrintsDataObject({ ...EMPTY_QUICK_PRINTS_DATA });
        setQuickPrintTemplateMeta(null);
        setQuickPrintTemplatesList([]);
        setSelectedQPTemplateId(null);
        setDailyFieldsSelection({ ...DEFAULT_DAILY_FIELDS });
        setFacilityIdAndTimeZone(null);
        setCalendarTypes([]);
        setSelectedCalendarTypes([]);
        setCategoiresList([]);
        setSelectedCalendarCategories([]);
        setRestaurantsList([]);
        setSelectedResturant(null);
    };

    // Re-fetch when account or corporate community changes. Provider stays mounted across sign-out/sign-in.
    useEffect(()=>{
        if (IsAuthChecking) return;

        if (!IsAuthenticated || !UserId) {
            resetFacilityScopedState();
            return;
        }

        resetFacilityScopedState();
        getCalendarList();
        getCalendarServiceCategoreis();
        handleResturantsList();
        getFacilityIdAndTimeZoneOfUser().then((facility) => {
            const facilityId = facility?.Facility ?? null;
            getQuickPrintsDatafromserver(facilityId);
            loadQuickPrintTemplateMeta(facilityId);
        });
        },[UserId, IsAuthenticated, IsAuthChecking, activeFacilityId]);

    const loadQuickPrintTemplateMeta = async (facilityId = FacilityIdAndTimeZone?.Facility ?? activeFacilityId) => {
        const requestId = ++templateLoadRequestIdRef.current;
        try {
            const templates = await handleGetSavedTemplates(facilityId);
            if (requestId !== templateLoadRequestIdRef.current) return [];
            const qpTemplates = (templates || []).filter((t) => t?.templateCategory === "quickprint");
            const list = qpTemplates.map((t) => {
                const meta = t.quickPrintMeta || {};
                const fromElements =
                    Array.isArray(t?.Data?.[0]?.Elements)
                        ? extractBodyFieldLayoutFromElements(t.Data[0].Elements)
                        : null;
                // Canvas element positions are source of truth (left/width placement).
                // Prefer re-extracting from Data so custom offsets (e.g. Long in middle, Short on left) survive.
                const bodyFieldLayout = fromElements || meta.bodyFieldLayout || undefined;
                return {
                    TemplateId: t.TemplateId,
                    Title: t.Title || "Quick Print Template",
                    quickPrintMeta: {
                        ...meta,
                        bodyFieldLayout,
                        bodyFieldOrder:
                            bodyFieldLayout?.order ||
                            meta.bodyFieldOrder ||
                            meta.bodyFieldLayout?.order ||
                            undefined,
                        dailyFields: meta.dailyFields || undefined,
                    },
                };
            });
            if (requestId !== templateLoadRequestIdRef.current) return [];
            setQuickPrintTemplatesList(list);
            return list;
        } catch (e) {
            if (requestId !== templateLoadRequestIdRef.current) return [];
            console.log("Error loading quick print template meta", e);
            return [];
        }
    };

    const updateTemplateDivider = async (templateId, newDividerTop, newDividerVisible) => {
        try {
            const templates = await handleGetSavedTemplates(FacilityIdAndTimeZone?.Facility ?? activeFacilityId);
            const record = (templates || []).find((t) => t.TemplateId === templateId);
            if (!record) return false;
            const updatedRecord = {
                ...record,
                quickPrintMeta: {
                    ...record.quickPrintMeta,
                    dividerTop: newDividerTop,
                    dividerVisible: newDividerVisible,
                },
            };
            const response = await API.put("Newsletterapp-user-templates", "/newsletter-user-templates", {
                body: { Data: [updatedRecord], TemplateId: templateId },
                response: true,
            });
            if (response) {
                await loadQuickPrintTemplateMeta();
                return true;
            }
            return false;
        } catch (e) {
            console.log("Error updating template divider", e);
            return false;
        }
    };
    
        const handleResturantsList=async()=>{
            const facility = await getFacilityIdAndTimeZoneOfUser();
            if (!facility?.Facility) {
              setRestaurantsList([]);
              setSelectedResturant(null);
              return;
            }
            let resturantsList=await getRestaurantsList(facility.Facility);
            setRestaurantsList(resturantsList);
            if(resturantsList.length>0)
            {
              setSelectedResturant(resturantsList[0].value);
            } else {
              setSelectedResturant(null);
            }
            };
        // get Calendar Categories
         const getCalendarServiceCategoreis=async()=>{
            try{
                LoaderController.show();
                const categorieslist=await getCalendarCategories();
                console.log("Categories list is ",categorieslist);
              let categoreislist=[];
              for(let category of categorieslist)
              {
                categoreislist.push({value:category,label:category});
              }
              
              setCategoiresList(categoreislist);
              setSelectedCalendarCategories([categoreislist[1]]);

            }catch(e){
                console.log("Error in getting calendar categories",e);
            }finally{
                LoaderController.hide();
            }

             
            };
        // get Calendar Types 
        const getCalendarList=async()=>{
            try{
                LoaderController.show();
                const calendarlist=await getCalendarTypes();
            let CalendarList=[];
            for(let calendaritem of calendarlist )
            {
              CalendarList.push({value:calendaritem._id,label:calendaritem.Name});
            }
            setCalendarTypes(CalendarList);
           setSelectedCalendarTypes([CalendarList[calendarlist.length-1]]);
            }catch(error){
                console.log("Error in getting calendar types",error);
                setCalendarTypes([]);
                setSelectedCalendarTypes([]);
            }finally{
                LoaderController.hide();
            }
            
          }



    // get Facility Id and Timezone 
      const getFacilityIdAndTimeZoneOfUser=async()=>{
        
        try {
            LoaderController.show();
            const ctx = await resolveFacilityContext();
            if (ctx?.facilityId) {
              const data = {
                Facility: ctx.facilityId,
                FacilityTimeZone: ctx.timezone,
              };
              setFacilityIdAndTimeZone(data);
              return data;
            }
            setFacilityIdAndTimeZone(null);
            return null;
       } catch (error) {
         console.log(error);
         setFacilityIdAndTimeZone(null);
         return null;
       } finally{
            LoaderController.hide();
       }
      };
    const getQuickPrintsDatafromserver=async(facilityId = FacilityIdAndTimeZone?.Facility ?? null)=>{

        try{
            LoaderController.show();
            let quickprintsresponse= await getQuickPrintsData(facilityId);
            if(quickprintsresponse!==null)
            {
            console.log("response for get quick prints data",quickprintsresponse);
            const serverData = quickprintsresponse.data || {};
            const normalizedLogoImage =
              typeof serverData.LogoImage === "string" &&
              serverData.LogoImage.trim() !== "" &&
              serverData.LogoImage.trim().toLowerCase() !== "null" &&
              serverData.LogoImage.trim().toLowerCase() !== "undefined"
                ? serverData.LogoImage
                : "";
            setQuickPrintsDataObject({
              ...serverData,
              LogoImage: normalizedLogoImage,
              LogoImageName: normalizedLogoImage ? serverData.LogoImageName : "",
            });
            }
            
        }catch(error){
            console.log("Error in getting Quick Prints data",error)
        } finally{
            LoaderController.hide();
        }   

    };

    const handleColorChange=(e,value)=>{
        if(value==="Color1")
        {
            setQuickPrintsDataObject((prev)=>({...prev,Color1:e.target.value}))
        }
        else
        {
            setQuickPrintsDataObject((prev)=>({...prev,Color2:e.target.value}))
        }
    }
    const handleTextAndSubTextChange=(e,from)=>{
        if(from==="Text")
        {
            setQuickPrintsDataObject((prev)=>({...prev,Text:e.target.value}))
        }
        else
        {
            const charlength=Array.from(e.target.value).length;
            if(charlength>185)
            {
                return;
            }
            setQuickPrintsDataObject((prev)=>({...prev,SubText:e.target.value}))
        }
    };

 

    // Logo Upload Handler
    const handleLogoUpload = (e) => {
        let file = e.target.files[0];
        if(file)
        {
          let filesize=(file.size / 1024 / 1024).toFixed(3);
          console.log("File size is ",filesize)
          if(filesize>10)
          {
            showNotification('Your image is too large to upload, please upload a smaller sized image of under 10MB.', 'error', 4000, 'top-center');
            return;
          }
       
      let reader=new FileReader();
      reader.readAsDataURL(e.target.files[0])
      
      reader.onload=async()=>{
        setQuickPrintsDataObject((prev)=>({...prev,LogoImageFileObject:file,LogoImage:reader.result,LogoImageName:e.target.files[0].name,LogoImageContentType:e.target.files[0].type}))
      }
    }
    }

    const handleRemoveLogoUpload=()=>{
        setQuickPrintsDataObject((prev)=>({...prev,LogoImage:'',LogoImageName:''}))
        LogoImageRef.current.value="";
    };


    const handleSubmitQuickPrintsData=async(e)=>{
        e.preventDefault();
            
                if(QuickPrintsDataObject.LogoImage==="" || QuickPrintsDataObject.Color1==="" || QuickPrintsDataObject.Color2==="" || QuickPrintsDataObject.SubText==="")
                {
                    showNotification('All Fields are required , Logo image, color1, color2 and Subtext', 'error', 4000, 'top-center');
                    return;
                }

                try{
                    LoaderController.show()
                    let quickprintdataasRequestBody=QuickPrintsDataObject;
                    delete quickprintdataasRequestBody.LogoImage;

                     let quickprintsdata= await createQuickPrintsData(quickprintdataasRequestBody, FacilityIdAndTimeZone?.Facility ?? null);
                     quickprintsdata=quickprintsdata.data;
                     console.log("Quick Prints data",quickprintsdata)
                    if(quickprintsdata!==null)
                    {
                        // upload image to s3 using presigned URL 

                        const logoresult=await uploadImageToS3(quickprintsdata.PreSignedUrl,QuickPrintsDataObject.LogoImageFileObject,QuickPrintsDataObject.LogoImageContentType);
                        if(logoresult===true)
                        {
                            setQuickPrintsDataObject((prev) => ({
                                ...prev,
                                LogoImage: quickprintsdata.LogoImage,
                                LogoImageFileObject: null,
                                LogoImageName: quickprintsdata.LogoImageName,
                                ObjectId: quickprintsdata.ObjectId,
                                createdBy:quickprintsdata.createdBy,
                              }));
                            showNotification('Style saved successfully', 'success', 4000, 'bottom-center');
                        } else {
                            showNotification('Error in creating Quick Prints data', 'error', 4000, 'bottom-center');
                        }




                    }
                }catch(error){
                    console.log("Error in creating Quick Prints data",error)
                    showNotification('Error in creating Quick Prints data', 'error', 4000, 'bottom-center');
                } finally{
                    LoaderController.hide();
                }
            
    };


    // handle update QuickPrints Data

    const handleUpdateQuickPritsData=async(e)=>{
        e.preventDefault();
        if(QuickPrintsDataObject.LogoImage==="" || QuickPrintsDataObject.Color1==="" || QuickPrintsDataObject.Color2==="" || QuickPrintsDataObject.SubText==="")
        {
            showNotification('All Fields are required , Logo image, color1, color2 and  Subtext', 'error', 4000, 'top-center');
            return;
        }

        if(QuickPrintsDataObject.LogoImageFileObject && QuickPrintsDataObject.LogoImageFileObject!==null)
        {
           
            try{
                LoaderController.show();
            
            let quickprintsdata=await updateQuickPrintsData(QuickPrintsDataObject, FacilityIdAndTimeZone?.Facility ?? null);
            quickprintsdata=quickprintsdata.data;
            console.log("Quick Prints data",quickprintsdata)
           if(quickprintsdata!==null)
           {
               // upload image to s3 using presigned URL 

               const logoresult=await uploadImageToS3(quickprintsdata.PreSignedUrl,QuickPrintsDataObject.LogoImageFileObject,QuickPrintsDataObject.LogoImageContentType);
               if(logoresult===true)
               {
                   setQuickPrintsDataObject((prev) => ({
                       ...prev,
                       LogoImage: quickprintsdata.LogoImage,
                       LogoImageFileObject: null,
                       LogoImageName: quickprintsdata.LogoImageName,
                       ObjectId: quickprintsdata.ObjectId,
                     }));
                   showNotification('Style saved successfully', 'success', 4000, 'bottom-center');
               } else {
                   showNotification('Error updating Quick Prints data', 'error', 4000, 'bottom-center');
               }
           }
        }
           catch(error){
            console.log("Error updating the quict prints data",error);
            showNotification('Error updating Quick Prints data', 'error', 4000, 'bottom-center');
        } finally{
            LoaderController.hide();
        }
            return;
        }
        else{
            try{
                LoaderController.show();
                let QuickPrintsDataforUpdate=QuickPrintsDataObject;
                const LogoImage=QuickPrintsDataforUpdate.LogoImage;
                delete QuickPrintsDataforUpdate.LogoImageFileObject;
                delete QuickPrintsDataforUpdate.LogoImage;
                let quickprintsdata=await updateQuickPrintsData(QuickPrintsDataforUpdate, FacilityIdAndTimeZone?.Facility ?? null);
                if(quickprintsdata.data!==null)
                {
                    console.log("updated data is",quickprintsdata.data);
                    setQuickPrintsDataObject((prev) => ({
                        ...prev,
                        LogoImage:LogoImage,
                        ...quickprintsdata.data,
                        LogoImageFileObject: null,
                      }));
                    showNotification('Style saved successfully', 'success', 4000, 'bottom-center');
                } else {
                    showNotification('Error updating Quick Prints data', 'error', 4000, 'bottom-center');
                }
            }catch(error)
            {
                console.log("error updating Quickt print data",error);
                showNotification('Error updating Quick Prints data', 'error', 4000, 'bottom-center');
            }finally{
                LoaderController.hide();
            }


            return;
        }
        
    };

    let ContextData={
        QuickPrintsDataObject,
        QuickPrintTemplateMeta,
        setQuickPrintTemplateMeta,
        DailyFieldsSelection,
        setDailyFieldsSelection,
        QuickPrintTemplatesList,
        selectedQPTemplateId,
        setSelectedQPTemplateId,
        LogoImageRef,
        FacilityIdAndTimeZone,
        CalendarTypes,
        SelectedCalendarTypes,
        CategoiresList,
        SelectedCalendarCategories,
        RestaurantsList,
        SelectedResturant,
        setSelectedResturant,
        setSelectedCalendarTypes,
        setSelectedCalendarCategories,
        handleLogoUpload,
        handleColorChange,
        handleTextAndSubTextChange,
        handleRemoveLogoUpload,
        handleSubmitQuickPrintsData,
        handleUpdateQuickPritsData,
        updateTemplateDivider,
        loadQuickPrintTemplateMeta,

    }
  return (
    <QuickPrintsContext.Provider value={ContextData}>
    {children}
   </QuickPrintsContext.Provider>
  )
  }
   
